import Vue from 'vue';

const isServer = Vue.prototype.$isServer;
const SPECIAL_CHARS_REGEXP = /([\:\-\_]+(.))/g;
const MOZ_HACK_REGEXP = /^moz([A-Z])/;
const ieVersion = isServer ? 0 : Number(document.documentMode);

export function isDef(val) {
	return val !== undefined && val !== null;
}
export function isKorean(text) {
	const reg = /([(\uAC00-\uD7AF)|(\u3130-\u318F)])+/gi;
	return reg.test(text);
}

export function merge(target) {
	for (let i = 1, j = arguments.length; i < j; i++) {
		let source = arguments[i] || {};
		for (let prop in source) {
			if (source.hasOwnProperty(prop)) {
				let value = source[prop];
				if (value !== undefined) {
					target[prop] = value;
				}
			}
		}
	}

	return target;
};

const hasOwnProperty = Object.prototype.hasOwnProperty;

export function noop() { };

export function hasOwn(obj, key) {
	return hasOwnProperty.call(obj, key);
};

function extend(to, _from) {
	for (let key in _from) {
		to[key] = _from[key];
	}
	return to;
};

export function toObject(arr) {
	var res = {};
	for (let i = 0; i < arr.length; i++) {
		if (arr[i]) {
			extend(res, arr[i]);
		}
	}
	return res;
};

export const getValueByPath = function (object, prop) {
	prop = prop || '';
	const paths = prop.split('.');
	let current = object;
	let result = null;
	for (let i = 0, j = paths.length; i < j; i++) {
		const path = paths[i];
		if (!current) break;

		if (i === j - 1) {
			result = current[path];
			break;
		}
		current = current[path];
	}
	return result;
};

export function getPropByPath(obj, path, strict) {
	let tempObj = obj;
	path = path.replace(/\[(\w+)\]/g, '.$1');
	path = path.replace(/^\./, '');

	let keyArr = path.split('.');
	let i = 0;
	for (let len = keyArr.length; i < len - 1; ++i) {
		if (!tempObj && !strict) break;
		let key = keyArr[i];
		if (key in tempObj) {
			tempObj = tempObj[key];
		} else {
			if (strict) {
				throw new Error('please transfer a valid prop path to form item!');
			}
			break;
		}
	}
	return {
		o: tempObj,
		k: keyArr[i],
		v: tempObj ? tempObj[keyArr[i]] : null
	};
};

export const generateId = function () {
	return Math.floor(Math.random() * 10000);
};

export const valueEquals = (a, b) => {
	// see: https://stackoverflow.com/questions/3115982/how-to-check-if-two-arrays-are-equal-with-javascript
	if (a === b) return true;
	if (!(a instanceof Array)) return false;
	if (!(b instanceof Array)) return false;
	if (a.length !== b.length) return false;
	for (let i = 0; i !== a.length; ++i) {
		if (a[i] !== b[i]) return false;
	}
	return true;
};

export const escapeRegexpString = (value = '') => String(value).replace(/[|\\{}()[\]^$+*?.]/g, '\\$&');

// TODO: use native Array.find, Array.findIndex when IE support is dropped
export const arrayFindIndex = function (arr, pred) {
	for (let i = 0; i !== arr.length; ++i) {
		if (pred(arr[i])) {
			return i;
		}
	}
	return -1;
};

export const arrayFind = function (arr, pred) {
	const idx = arrayFindIndex(arr, pred);
	return idx !== -1 ? arr[idx] : undefined;
};

// coerce truthy value to array
export const coerceTruthyValueToArray = function (val) {
	if (Array.isArray(val)) {
		return val;
	} else if (val) {
		return [val];
	} else {
		return [];
	}
};

export const isIE = function () {
	return !Vue.prototype.$isServer && !isNaN(Number(document.documentMode));
};

export const isEdge = function () {
	return !Vue.prototype.$isServer && navigator.userAgent.indexOf('Edge') > -1;
};


/* istanbul ignore next */
export const on = (function () {
	if (!isServer && document.addEventListener) {
		return function (element, event, handler) {
			if (element && event && handler) {
				element.addEventListener(event, handler, false);
			}
		};
	} else {
		return function (element, event, handler) {
			if (element && event && handler) {
				element.attachEvent('on' + event, handler);
			}
		};
	}
})();

/* istanbul ignore next */
export const off = (function () {
	if (!isServer && document.removeEventListener) {
		return function (element, event, handler) {
			if (element && event) {
				element.removeEventListener(event, handler, false);
			}
		};
	} else {
		return function (element, event, handler) {
			if (element && event) {
				element.detachEvent('on' + event, handler);
			}
		};
	}
})();


let scrollBarWidth;
export function scrollbarWidth() {
	if (Vue.prototype.$isServer) return 0;
	if (scrollBarWidth !== undefined) return scrollBarWidth;

	const outer = document.createElement('div');
	outer.className = 'poi-scrollbar__wrap';
	outer.style.visibility = 'hidden';
	outer.style.width = '100px';
	outer.style.position = 'absolute';
	outer.style.top = '-9999px';
	document.body.appendChild(outer);

	const widthNoScroll = outer.offsetWidth;
	outer.style.overflow = 'scroll';

	const inner = document.createElement('div');
	inner.style.width = '100%';
	outer.appendChild(inner);

	const widthWithScroll = inner.offsetWidth;
	outer.parentNode.removeChild(outer);
	scrollBarWidth = widthNoScroll - widthWithScroll;

	return scrollBarWidth;
};


/* istanbul ignore next */
const resizeHandler = function (entries) {
	for (let entry of entries) {
		const listeners = entry.target.__resizeListeners__ || [];
		if (listeners.length) {
			listeners.forEach(fn => {
				fn();
			});
		}
	}
};


const isWindow = typeof window === 'undefined';
/* istanbul ignore next */
export const addResizeListener = function (element, fn) {
	if (isWindow) return;
	if (!element.__resizeListeners__) {
		element.__resizeListeners__ = [];
		element.__ro__ = new ResizeObserver(resizeHandler);
		element.__ro__.observe(element);
	}
	element.__resizeListeners__.push(fn);
};

/* istanbul ignore next */
export const removeResizeListener = function (element, fn) {
	if (!element || !element.__resizeListeners__) return;
	element.__resizeListeners__.splice(element.__resizeListeners__.indexOf(fn), 1);
	if (!element.__resizeListeners__.length) {
		element.__ro__.disconnect();
	}
};


export function scrollIntoView(container, selected) {
	if (Vue.prototype.$isServer) return;

	if (!selected) {
		container.scrollTop = 0;
		return;
	}

	const offsetParents = [];
	let pointer = selected.offsetParent;
	while (pointer && container !== pointer && container.contains(pointer)) {
		offsetParents.push(pointer);
		pointer = pointer.offsetParent;
	}
	const top = selected.offsetTop + offsetParents.reduce((prev, curr) => (prev + curr.offsetTop), 0);
	const bottom = top + selected.offsetHeight;
	const viewRectTop = container.scrollTop;
	const viewRectBottom = viewRectTop + container.clientHeight;

	if (top < viewRectTop) {
		container.scrollTop = top;
	} else if (bottom > viewRectBottom) {
		container.scrollTop = bottom - container.clientHeight;
	}
}


/*防抖-HZW
	immediate : 是否立即执行
	delay: 延时时间
	callback: 回调函数
*/

var isDelay = false;
var timer = null;
export function debounce(immediate, delay, callback) {
	if(immediate&&!isDelay){
		
		setTimeout(()=>{
			isDelay = false;
		},delay)
		isDelay = true;
		return callback();
	}
	if(!immediate){
		clearTimeout(timer);
		timer = setTimeout(()=>{
			return callback();
		},delay)
	}
}

export function deepCopy(obj1) {
    let type = Object.prototype.toString.call(obj1)
    let resObj = undefined
        //引用类型
    if (type.includes('object')) {
        if (type === '[object Array]') {
            resObj = []
            for (let i = 0; i < obj1.length; i++) {
                if (typeof obj1[i] == 'object') {
                    resObj[i] = deepCopy(obj1[i])
                } else {
                    resObj[i] = obj1[i]
                }
            }
        }
        if (type === '[object Object]') {
            resObj = {}
            for (let key in obj1) {
                if (typeof obj1[key] == 'object') {
                    resObj[key] = deepCopy(obj1[key])
                } else {
                    resObj[key] = obj1[key]
                }
            }
        }
    } else {
        //基本类型
        resObj = obj1
    }
    return resObj
}


//查找对象或数组的匹配数据
export function findArrObj(obj,arr,prop,type="=="){
    let resObj = null, str='', jude="";
    if(Array.isArray(obj)){
        resObj = [];
        jude = type=='=='?'||':'&&';

        str = `item['${prop}']${type}${arr.join(',').replace(/\,/g,`${jude} item['${prop}']${type}`)}`;
		str = type=='=='?str.replace(/\=\=/g,`=="`).replace(/\|\|/g,`"||`)+'"':str.replace(/\!\=/g,`!="`).replace(/\&\&/g,`"&&`)+'"';
        resObj = obj.filter(item=>eval(str));
    }else{
        resObj ={};
        //如果为对象
        if(prop) type = prop;
        jude = type=='=='?'||':'&&';

        for(let key in obj){
            str = `"${key}"${type}${arr.join(',').replace(/\,/g,`${jude} "${key}"${type}`)}`;
            str = type=='=='?str.replace(/\=\=/g,`=="`).replace(/\|\|/g,`"||`)+'"':str.replace(/\!\=/g,`!="`).replace(/\&\&/g,`"&&`)+'"';

            if(eval(str)) resObj[key] = obj[key];
            
        }
	}
    return resObj;
}

//获取拥有指定key的列表
export function propKeyList(obj, prop) {
    let propList = { equal: [], rev: [] };
    obj.forEach(data => {
        let noEqual = false;
        for (let key in data) {
            if (key == prop) {
                propList.equal.push(data);
                noEqual = true;
                return;
            }
        }!noEqual && propList.rev.push(data);
    })
    return propList;
}

//获取对象指定属性
export function objToArr(obj, prop) {
    let propArr = [];
    obj.forEach(val => {
        propArr.push(val[prop]);
    })
    return propArr
}
